import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await axios.post(`${API}/auth/request-password-reset`, {
        email: email.trim().toLowerCase(),
        frontend_origin: window.location.origin,
      });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16 flex items-center">
      <div className="max-w-md mx-auto w-full px-6">
        <div className="mb-6">
          <span className="enf-brand-pill inline-flex items-center">
            <img
              src="/brand/enferno-wordmark-nav@2x.png"
              srcSet="/brand/enferno-wordmark-nav.png 1x, /brand/enferno-wordmark-nav@2x.png 2x"
              alt="Enferno"
              className="h-12 w-auto"
              loading="eager"
            />
          </span>
        </div>

        <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-2">Account recovery</div>
        <h1 className="font-display text-5xl tracking-wide leading-none mb-3">Forgot it?</h1>
        <p className="text-enf-text-secondary mb-8 text-sm">Drop your email and we&apos;ll send a reset link.</p>

        {sent ? (
          <div
            className="px-4 py-5 bg-emerald-950/40 border border-emerald-900/60 rounded-md"
            data-testid="forgot-password-sent"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-semibold text-emerald-200 mb-1">Check your inbox</div>
                <p className="text-sm text-enf-text-secondary leading-relaxed">
                  If an Enferno account exists for <span className="text-enf-text-primary">{email}</span>, you&apos;ll
                  get a reset link within a minute. The link expires in 15 minutes.
                </p>
                <p className="text-xs text-enf-text-muted leading-relaxed mt-2">
                  Don&apos;t see it? First-time emails from us sometimes land in spam or promotions — check those folders before trying again.
                </p>
              </div>
            </div>
            <div className="mt-5 text-sm text-enf-text-muted">
              Back to{' '}
              <Link to="/login" className="text-enf-role-info hover:text-enf-text-primary font-semibold">sign in</Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Email</span>
              <input
                type="email" required autoComplete="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                data-testid="forgot-password-email"
              />
            </label>

            {error && (
              <div className="px-3 py-2 bg-enf-role-danger/10 border border-enf-role-danger/40 rounded-md text-sm text-enf-role-danger" data-testid="forgot-password-error">{error}</div>
            )}

            <button
              type="submit" disabled={busy}
              className="w-full py-3 bg-enf-accent hover:bg-enf-accent-hover disabled:bg-enf-surface-elevated disabled:text-enf-text-muted disabled:cursor-not-allowed rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
              data-testid="forgot-password-submit"
            >
              {busy ? 'Sending...' : <>Send reset link <ArrowRight className="w-4 h-4" /></>}
            </button>

            <div className="text-center text-sm text-enf-text-muted pt-2">
              Remembered it?{' '}
              <Link to="/login" className="text-enf-role-info hover:text-enf-text-primary font-semibold">Sign in</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
